/**
 * MOCK — Vista de prueba para los botones de acción de EventCard
 * Acceder en: /mock-buttons
 */
import moment from 'moment'
import 'moment/locale/es'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './HomeView.scss'
import './MockButtons.scss'

moment.locale('es')

// ─── Datos mock ────────────────────────────────────────────────────────────────
const NOW = moment()

const MOCK_EVENTS = [
  {
    _id: '1',
    title: 'CrossFit RX',
    start: NOW.clone().add(2, 'hours').toISOString(),
    end: NOW.clone().add(3, 'hours').toISOString(),
    inscriptionCount: 10,
    maxInscriptions: 24,
    isFull: false,
    isParticipant: false,
    isWaitlist: false,
    categoryColor: '#2440ab',
    _label: '✅ Disponible — puede inscribirse',
  },
  {
    _id: '2',
    title: 'Yoga Flow',
    start: NOW.clone().add(2, 'hours').toISOString(),
    end: NOW.clone().add(3, 'hours').toISOString(),
    inscriptionCount: 18,
    maxInscriptions: 24,
    isFull: false,
    isParticipant: true,
    isWaitlist: false,
    categoryColor: '#10b981',
    _label: '🟢 Inscrito — puede abandonar',
  },
  {
    _id: '3',
    title: 'HIIT Express',
    start: NOW.clone().add(2, 'hours').toISOString(),
    end: NOW.clone().add(3, 'hours').toISOString(),
    inscriptionCount: 24,
    maxInscriptions: 24,
    isFull: true,
    isParticipant: false,
    isWaitlist: false,
    categoryColor: '#f77f00',
    _label: '🔴 Llena — puede apuntarse a espera',
  },
  {
    _id: '4',
    title: 'Spinning',
    start: NOW.clone().add(2, 'hours').toISOString(),
    end: NOW.clone().add(3, 'hours').toISOString(),
    inscriptionCount: 24,
    maxInscriptions: 24,
    isFull: true,
    isParticipant: false,
    isWaitlist: true,
    categoryColor: '#f77f00',
    _label: '🟠 En lista de espera — puede salir',
  },
  {
    _id: '5',
    title: 'Functional',
    start: NOW.clone().add(72, 'hours').toISOString(),
    end: NOW.clone().add(73, 'hours').toISOString(),
    inscriptionCount: 5,
    maxInscriptions: 24,
    isFull: false,
    isParticipant: false,
    isWaitlist: false,
    categoryColor: '#6366f1',
    _label: '🔒 Bloqueado — faltan más de 48h (contador vivo)',
  },
]

// ─── Componente acción (mismo que HomeView pero sin llamadas reales) ─────────
const MockEventCard = ({ event }) => {
  const startTime = moment(event.start).utc().format('HH:mm')
  const endTime = moment(event.end).utc().format('HH:mm')
  const fillPercent = (event.inscriptionCount / event.maxInscriptions) * 100
  const tooEarly = moment().add(48, 'hours').isBefore(moment(event.start))

  const [lockTime, setLockTime] = useState('')
  const lockRef = useRef(null)
  useEffect(() => {
    if (!tooEarly) return
    const openAt = moment(event.start).subtract(48, 'hours')
    const tick = () => {
      const diff = openAt.diff(moment())
      if (diff <= 0) {
        setLockTime('')
        clearInterval(lockRef.current)
        return
      }
      const d = moment.duration(diff)
      const h = Math.floor(d.asHours())
      const m = d.minutes()
      const s = d.seconds()
      setLockTime(
        `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`
      )
    }
    tick()
    lockRef.current = setInterval(tick, 1000)
    return () => clearInterval(lockRef.current)
  }, [event.start, tooEarly])

  const noop = e => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <div className="mock-card-wrapper">
      <span className="mock-card-label">{event._label}</span>
      <div className="event-card">
        <div
          className="event-card__main"
          style={{ textDecoration: 'none', display: 'flex', flex: 1 }}
        >
          <div
            className="event-card__color"
            style={{ backgroundColor: event.categoryColor }}
          />
          <div className="event-card__content">
            <div className="event-card__top">
              <div className="event-card__info">
                <h4 className="event-card__title">{event.title}</h4>
                <p className="event-card__time">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  {startTime} - {endTime}
                </p>
              </div>
              <div className="event-card__badges">
                {event.isParticipant && (
                  <span
                    style={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: '#10b981',
                      background: '#d1fae5',
                      padding: '2px 6px',
                      borderRadius: 99,
                    }}
                  >
                    ✓ Inscrit@
                  </span>
                )}
              </div>
            </div>
            <div className="event-card__bottom">
              <div className="event-card__capacity">
                <div className="event-card__bar">
                  <div
                    className={`event-card__fill ${event.isFull ? 'event-card__fill--full' : ''}`}
                    style={{ width: `${Math.min(fillPercent, 100)}%` }}
                  />
                </div>
                <span
                  className={`event-card__spots ${event.isFull ? 'event-card__spots--full' : ''}`}
                >
                  {event.isFull
                    ? 'Completa'
                    : `${event.inscriptionCount}/${event.maxInscriptions}`}
                </span>
              </div>
              <svg
                className="event-card__arrow"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </div>
          </div>
        </div>

        {/* Botón de acción */}
        <div className="event-card__actions">
          {event.isParticipant ? (
            <button
              className="event-card__action-btn event-card__action-btn--leave"
              onClick={noop}
              title="Abandonar clase"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Abandonar</span>
            </button>
          ) : event.isFull ? (
            <button
              className={`event-card__action-btn ${event.isWaitlist ? 'event-card__action-btn--waitlist-leave' : 'event-card__action-btn--waitlist'}`}
              onClick={noop}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
              <span>{event.isWaitlist ? 'Salir cola' : 'En espera'}</span>
            </button>
          ) : tooEarly ? (
            <div className="event-card__action-btn event-card__action-btn--locked">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span className="event-card__lock-time">{lockTime}</span>
            </div>
          ) : (
            <button
              className="event-card__action-btn event-card__action-btn--join"
              onClick={noop}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <line x1="20" y1="8" x2="20" y2="14" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
              <span>Inscribirse</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Vista principal mock ───────────────────────────────────────────────────
const MockButtons = () => {
  return (
    <div className="mock-view page">
      <div className="mock-header">
        <Link to="/" className="mock-back">
          ← Volver a la app
        </Link>
        <h1>Mock · Botones de acción</h1>
        <p>Vista de prueba — los botones no realizan llamadas reales</p>
      </div>
      <div className="mock-list">
        {MOCK_EVENTS.map(event => (
          <MockEventCard key={event._id} event={event} />
        ))}
      </div>
    </div>
  )
}

export default MockButtons
